import { atom, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback } from "react";
import type { ApprovalMode, PageEntity, PageSelection } from "@/api/assistant";
import { useCurrentProject } from "@/hooks/use-current-project";

const persisted = <T>(key: string, initial: T) =>
	atomWithStorage<T>(key, initial, undefined, { getOnInit: true });

export const assistantPanelOpenAtom = persisted("gridcast:assistant-panel", false);
/** Docked beside the page instead of floating above it. */
export const assistantDockedAtom = persisted("gridcast:assistant-docked", false);
/** Key for threads opened outside any project in `activeThreadsAtom`. */
export const WORKSPACE_SCOPE = "workspace";
/** Last open thread per surface (project id, or `WORKSPACE_SCOPE`), shared by the panel and page. */
export const activeThreadsAtom = persisted<Record<string, string>>(
	"gridcast:assistant-threads",
	{},
);
export const assistantModelAtom = persisted<string | null>("gridcast:assistant-model", null);
/** Fallback when the workspace setting (`/assistant/settings`) is unavailable. */
export const approvalModeAtom = persisted<ApprovalMode>("gridcast:assistant-approval", "auto_safe");

/**
 * Records the current page has selected, sent as run context. Pages own it: set it as the
 * selection changes and reset it to `null` on unmount.
 */
export const assistantSelectionAtom = atom<PageSelection | null>(null);
/** Other records the page shows (max 5), same ownership rule as `assistantSelectionAtom`. */
export const assistantRelatedAtom = atom<PageEntity[] | null>(null);

/** Something another page wants the assistant to do (e.g. "Generate with AI"). */
export type AssistantRequest = {
	nonce: number;
	/** Sent right away in a new thread. */
	prompt?: string;
	/** Attached as context to the request's prompt, or to the next message. */
	entity?: PageEntity;
	label?: string;
};
export const assistantRequestAtom = atom<AssistantRequest | null>(null);

export type OpenAssistantOptions = {
	prompt?: string;
	entity?: PageEntity;
	newThread?: boolean;
	label?: string;
};

/** Opens the ⌘J assistant, optionally in a fresh thread, sending `prompt` about `entity`. */
export function useOpenAssistant() {
	const setRequest = useSetAtom(assistantRequestAtom);
	const setOpen = useSetAtom(assistantPanelOpenAtom);
	const setActive = useSetAtom(activeThreadsAtom);
	const { projectId } = useCurrentProject();
	return useCallback(
		({ prompt, entity, newThread, label }: OpenAssistantOptions = {}) => {
			if (newThread) {
				const scope = projectId ?? WORKSPACE_SCOPE;
				setActive(({ [scope]: _current, ...rest }) => rest);
			}
			if (prompt || entity) setRequest({ prompt, entity, label, nonce: Date.now() });
			setOpen(true);
		},
		[projectId, setActive, setRequest, setOpen],
	);
}

/** Opens the assistant in a fresh thread and sends `prompt`. */
export function useAskAssistant() {
	const open = useOpenAssistant();
	return useCallback(
		(prompt: string, label?: string) => open({ prompt, label, newThread: true }),
		[open],
	);
}
